import copy from '../copy';
import * as validators from '../helpers/validators';

const { errorMessages } = copy;

const isEmptyString = validators.checkString({ maxLength: 0 });
const isNonEmptyString = validators.checkString({ minLength: 1 });

export const intentTypes = {
    CONSUMER: 'CONSUMER',
    DEALER: 'DEALER',
    TRADE: 'TRADE',
} as const;
export type IntentValues = typeof intentTypes[keyof typeof intentTypes];

export const stepLabels = {
    DISPLAYED: 'displayed',
    COMPLETED: 'completed',
    SKIPPED: 'skipped',
} as const;

export const answerNames = {
    GIFT: 'GIFT',
} as const;

export const tradeApplicationInfoKey = 'tradeApplicationInfo' as const;
export const steps = {
    USER_TYPE_SELECTION: 'user_type_selection',
    CONSUMER_BENEFITS: 'consumer_benefits',
    CONSUMER_COMPLETE: 'consumer_complete',
    CONSUMER_INFO: 'consumer_info',
    DEALER_INFO: 'dealer_info',
    TRADE_APPLICATION_STEP_1: 'trade_application_step_1',
    TRADE_APPLICATION_STEP_2: 'trade_application_step_2',
    TRADE_APPLICATION_MORE_INFO: 'trade_application_more_info',
    TRADE_REFERRAL: 'trade_referral',
    TRADE_COMPLETE: 'trade_complete',
    PROMO_CODE: 'promo_code',
    USER_SURVEY: 'user_survey',
    USER_SURVEY_CONFIRMATION: 'user_survey_confirmation',
    USER_SURVEY_GIFT_CONFIRMATION: 'user_survey_gift_confirmation',
    MARKETING_OPT_IN: 'marketing_opt_in',
} as const;

export const gaLabels = {
    PAGEVIEW_LIMIT_REACHED: 'pageview limit reached',
} as const;

export const events = {
    USER_CHANGED: 'user_changed',
} as const;
export type Events = keyof typeof events;
export type EventValues = typeof events[Events];

const registrationLeadSourceLabel = 'Web: User Registration' as const;
const loginLeadSourceLabel = 'Web: User Login' as const;
const socialRegistrationSourceLabel = 'Web: Social Registration' as const;

const socialRegisterSourceLabel = {
    leadSourceLabel: socialRegistrationSourceLabel,
    tradeLeadSourceLabel: socialRegistrationSourceLabel,
};

const socialRegisterUserTypeSelection = {
    ...socialRegisterSourceLabel,
    startingStep: steps.USER_TYPE_SELECTION,
};

const socialRegisterMarketingOptIn = {
    ...socialRegisterSourceLabel,
    startingStep: steps.MARKETING_OPT_IN,
};

const socialRegisterUserSurvey = {
    ...socialRegisterSourceLabel,
    startingStep: steps.USER_SURVEY,
};

export const flowActions = {
    AUTO_PROMPT_REGISTER: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_TYPE_SELECTION,
    },
    LOGIN: {
        leadSourceLabel: loginLeadSourceLabel,
        startingStep: steps.USER_TYPE_SELECTION,
    },
    REGISTER: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_TYPE_SELECTION,
    },
    SOCIAL_REGISTER_FACEBOOK: socialRegisterUserTypeSelection,
    SOCIAL_REGISTER_GOOGLE: socialRegisterUserTypeSelection,
    SOCIAL_REGISTER_APPLE: socialRegisterUserTypeSelection,
    SOCIAL_REGISTER_FACEBOOK_MARKETING: socialRegisterMarketingOptIn,
    SOCIAL_REGISTER_GOOGLE_MARKETING: socialRegisterMarketingOptIn,
    SOCIAL_REGISTER_APPLE_MARKETING: socialRegisterMarketingOptIn,
    SOCIAL_REGISTER_FACEBOOK_USER_SURVEY: socialRegisterUserSurvey,
    SOCIAL_REGISTER_GOOGLE_USER_SURVEY: socialRegisterUserSurvey,
    SOCIAL_REGISTER_APPLE_USER_SURVEY: socialRegisterUserSurvey,
    SOCIAL_LOGIN_MARKETING: socialRegisterMarketingOptIn,
    TRADE_APPLICATION: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_APPLICATION_STEP_1,
    },
    TRADE_APPLICATION_STEP_2: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_APPLICATION_STEP_2,
    },
    SUSPECTED_TRADE_APPLICATION: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_APPLICATION_STEP_1,
    },
    TRADE_REAPPLY: {
        leadSourceLabel: 'Firmless: Reapplied',
        startingStep: steps.TRADE_APPLICATION_STEP_1,
    },
    TRADE_APPLICATION_MORE_INFO: {
        leadSourceLabel: 'Web: Trade Registration',
        startingStep: steps.TRADE_APPLICATION_MORE_INFO,
    },
    ALT_REGISTRATION_CONSUMER_FLOW: {
        leadSourceLabel: registrationLeadSourceLabel,
        startingStep: steps.USER_SURVEY,
    },
} as const;
export type FlowActions = keyof typeof flowActions;

export const footers = {
    ASK_LATER: 'ask_later_footer',
    REQUEST_PASSWORD_EMAIL: 'request_password_email_footer',
    LOGIN: 'login_footer',
    REGISTER: 'register_footer',
    SKIP_QUESTION: 'skip_question',
} as const;

export const tradeFormFields = {
    FIRST_NAME: 'FIRST_NAME',
    LAST_NAME: 'LAST_NAME',
    FIRM_NAME: 'FIRM_NAME',
    FIRM_WEBSITE: 'FIRM_WEBSITE',
    PHONE_NUMBER: 'PHONE_NUMBER',
    EMAIL: 'EMAIL',
    // CAM-461 SALESFORCE-2890
    // HAS_OFFICIAL_DOCS is a misnomer - it should really be "FOLLOW_UP_FOR_DOCUMENTATION"
    // but instead of doing a code change, which require coordinating with FE and SF,
    // this is going to stay as-is but noting that in SF, this indicates that the
    // Firm Management will follow-up with the Lead to get more documentation
    HAS_OFFICIAL_DOCS: 'HAS_OFFICIAL_DOCS',
    TRADE_DOCUMENT: 'TRADE_DOCUMENT',
    SOCIAL_DETAILS: 'SOCIAL_DETAILS',
    FIRM_SIZE: 'FIRM_SIZE',
    ROLE_AT_FIRM: 'ROLE_AT_FIRM',
    FIRM_TYPE: 'FIRM_TYPE',
    FIRM_ADDRESS: 'FIRM_ADDRESS',
    FIRM_POSTAL_CODE: 'FIRM_POSTAL_CODE',
    FIRM_COUNTRY: 'FIRM_COUNTRY',
    USER_EXISTS: 'USER_EXISTS',
    REASON_FOR_JOINING: 'REASON_FOR_JOINING',
    TRADE_USER_STATUS: 'TRADE_USER_STATUS',
    DESIGN_SERVICES: 'DESIGN_SERVICES',
} as const;

export const tradeFormFieldsConfig = {
    [tradeFormFields.FIRST_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.firstName,
        validator: validators.checkString({ minLength: 1, maxLength: 40 }),
    },
    [tradeFormFields.LAST_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.lastName,
        validator: validators.checkString({ minLength: 1, maxLength: 80 }),
    },
    [tradeFormFields.FIRM_NAME]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmName,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_WEBSITE]: {
        defaultValue: '',
        errorMessage: null,
        validator: validators.oneOf(isEmptyString, validators.isValidUrl),
    },
    [tradeFormFields.PHONE_NUMBER]: {
        defaultValue: '',
        errorMessage: errorMessages?.phoneNumber,
        validator: validators.checkString({ minLength: 9 }),
    },
    [tradeFormFields.EMAIL]: {
        defaultValue: '',
        errorMessage: errorMessages?.emailInvalid,
        validator: validators.all(
            validators.isValidEmail,
            validators.checkString({ maxLength: 80 })
        ),
    },
    [tradeFormFields.HAS_OFFICIAL_DOCS]: {
        defaultValue: false,
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.TRADE_DOCUMENT]: {
        defaultValue: null,
        errorMessage: errorMessages?.tradeDocumentInvalid,
        validator: (val: EventTarget & HTMLInputElement) => !!val,
    },
    [tradeFormFields.SOCIAL_DETAILS]: {
        defaultValue: null,
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.FIRM_SIZE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmSize,
        validator: isNonEmptyString,
    },
    [tradeFormFields.ROLE_AT_FIRM]: {
        defaultValue: '',
        errorMessage: errorMessages?.roleAtFirm,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_TYPE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmType,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_ADDRESS]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmAddress,
        validator: isNonEmptyString,
    },
    [tradeFormFields.FIRM_POSTAL_CODE]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmZipCode,
        validator: validators.all(isNonEmptyString, validators.isValidZipcode),
    },
    [tradeFormFields.FIRM_COUNTRY]: {
        defaultValue: '',
        errorMessage: errorMessages?.firmCountry,
        validator: isNonEmptyString,
    },
    [tradeFormFields.USER_EXISTS]: {
        defaultValue: false,
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.REASON_FOR_JOINING]: {
        defaultValue: '',
        errorMessage: errorMessages?.reasonForJoining,
        validator: isNonEmptyString,
    },
    [tradeFormFields.TRADE_USER_STATUS]: {
        defaultValue: '',
        errorMessage: null,
        validator: null,
    },
    [tradeFormFields.DESIGN_SERVICES]: {
        defaultValue: '',
        errorMessage: errorMessages?.designServicesInvalid,
        validator: isNonEmptyString,
    },
} as const;
export type TradeFormFields = keyof typeof tradeFormFieldsConfig;
